import { create } from 'zustand';
import keycloak from '@/keycloak';
import { ROUTES } from '@/config/routes';
import apiClient from '@/services/api';
import { authService } from '@/services/authService';
import type { OrganizationStatus, Permission } from '@/types/models';
import { getPrimaryRole, ROLE_PERMISSIONS } from '@/utils/auth';
import { extractOrganizationId } from '@/utils/tokenClaims';
import type { AcceptInviteCredentials } from '@/types/auth';

/**
 * Where the app asks what the current user may do.
 *
 * The standalone CMS bundle has no UMS, so it asks case-management instead —
 * which resolves the same permissions PermissionsGuard enforces. Both return
 * the same shape, so only the path differs.
 */
const ME_ENDPOINT =
  import.meta.env.VITE_APP_VARIANT === 'cms' ? '/case/cms-users/me' : '/ums/users/me';

interface User {
  id?: string;
  sub: string;
  email: string;
  organizationId: string;
  roles: string[];
  name?: string;
  preferred_username?: string;
  givenName?: string;
  familyName?: string;
  /** Uploaded profile picture. Lives in UMS, not in the Keycloak token. */
  avatarUrl?: string | null;
  /** Uploaded profile cover banner. Also UMS-side. */
  bannerUrl?: string | null;
  organizationStatus?: OrganizationStatus;
}

interface TokenPayload {
  sub?: string;
  email?: string;
  organizationId?: string;
  organization_id?: string;
  org_id?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  organizationStatus?: OrganizationStatus;
  organization_status?: OrganizationStatus;
  org_status?: OrganizationStatus;
  realm_access?: {
    roles?: string[];
  };
}

function getUserFromToken(): User | null {
  if (!keycloak.authenticated || !keycloak.tokenParsed) {
    return null;
  }

  const tokenParsed = (keycloak.tokenParsed ?? {}) as TokenPayload;
  const idTokenParsed = (keycloak.idTokenParsed ?? {}) as Record<string, unknown>;

  const organizationStatus =
    tokenParsed?.organizationStatus ??
    tokenParsed?.organization_status ??
    tokenParsed?.org_status ??
    'approved';
  const organizationId = extractOrganizationId(tokenParsed as Record<string, unknown>);

  const givenName = ((tokenParsed?.given_name || idTokenParsed?.given_name) as string) || undefined;
  const familyName = ((tokenParsed?.family_name || idTokenParsed?.family_name) as string) || undefined;
  const preferred_username = ((tokenParsed?.preferred_username || idTokenParsed?.preferred_username) as string) || undefined;
  const email = ((tokenParsed?.email || idTokenParsed?.email) as string) || '';
  const name =
    ((tokenParsed?.name || idTokenParsed?.name) as string) ||
    (givenName && familyName ? `${givenName} ${familyName}`.trim() : preferred_username);

  return {
    id: tokenParsed?.sub ?? '',
    sub: tokenParsed?.sub ?? '',
    email,
    organizationId,
    roles: tokenParsed?.realm_access?.roles ?? [],
    name,
    preferred_username,
    givenName,
    familyName,
    organizationStatus,
  };
}

interface AuthState {
  user: User | null;
  /** Resolved server-side from the user's roles. ['*'] for a platform admin. */
  permissions: string[];
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  /** keepOnError: leave the current permissions in place if the call fails. */
  refreshPermissions: (options?: { keepOnError?: boolean }) => Promise<void>;
  login: () => void;
  logout: () => void;
  register: (data: { orgName: string; orgSlug: string; contactFirst: string; contactLast: string; contactEmail: string }) => Promise<void>;
  acceptInvite: (credentials: AcceptInviteCredentials) => Promise<void>;
  getToken: () => Promise<string | null>;
  refreshSession: (minValidity?: number) => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const FOCUS_REFRESH_INTERVAL_MS = 30_000;
let focusRefreshInstalled = false;

/**
 * Re-pulls permissions when the tab comes back into focus, so an admin's edit
 * to this user's role reaches an already-open session without a reload.
 * Throttled, since focus and visibility events fire often.
 */
function refreshPermissionsOnFocus(refresh: () => void) {
  if (focusRefreshInstalled || typeof window === 'undefined') return;
  focusRefreshInstalled = true;

  let last = Date.now();
  const onFocus = () => {
    if (document.visibilityState === 'hidden') return;
    if (Date.now() - last < FOCUS_REFRESH_INTERVAL_MS) return;
    last = Date.now();
    refresh();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: [],
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  error: null,

  initialize: async () => {
    try {
      const user = getUserFromToken();
      const existingUser = get().user;
      const initialUser = user
        ? {
            ...user,
            ...(existingUser?.name ? { name: existingUser.name } : {}),
            avatarUrl: existingUser?.avatarUrl ?? null,
            bannerUrl: existingUser?.bannerUrl ?? null,
          }
        : null;
      set({ user: initialUser, isAuthenticated: Boolean(user) });
      if (user) {
        await get().refreshPermissions();
        refreshPermissionsOnFocus(() => {
          if (get().isAuthenticated) void get().refreshPermissions({ keepOnError: true });
        });
      }
    } catch (err) {
      console.error('Keycloak auth sync failed:', err);
      set({ user: null, permissions: [], isAuthenticated: false });
    } finally {
      set({ isInitialized: true, isLoading: false });
    }
  },

  /**
   * Pulls the permissions the server says this user has. Called on init, and
   * worth calling again after anything that changes them (an admin editing
   * your role) so the UI catches up without a re-login.
   *
   * A failure here leaves permissions empty, which hides privileged UI rather
   * than showing it. The API is the real gate either way.
   */
  refreshPermissions: async (options) => {
    try {
      const me = await apiClient.get<{
        permissions?: string[];
        displayName?: string | null;
        avatarUrl?: string | null;
        bannerUrl?: string | null;
      }>(ME_ENDPOINT);
      // displayName is edited in-app and stored in UMS, so it is authoritative
      // over the Keycloak token's `name` claim, which only changes on re-login.
      const displayName = me?.displayName?.trim();
      set((state) => ({
        permissions: me?.permissions ?? [],
        user: state.user
          ? {
              ...state.user,
              ...(displayName ? { name: displayName } : {}),
              avatarUrl: me?.avatarUrl ?? null,
              bannerUrl: me?.bannerUrl ?? null,
            }
          : state.user,
      }));
    } catch (err) {
      console.error('Failed to load permissions:', err);
      if (!options?.keepOnError) set({ permissions: [] });
    }
  },

  register: async (data) => {
    await apiClient.post('/ums/auth/request-access', data, { skipAuth: true });
  },

  acceptInvite: async (credentials: AcceptInviteCredentials) => {
    set({ isLoading: true, error: null });
    try {
      await authService.acceptInvite(credentials);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept invitation';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  login: () => {
    void keycloak.login({
      // Always redirect to a clean route to avoid reusing callback fragments.
      redirectUri: `${window.location.origin}${ROUTES.HOME}`,
    });
  },

  logout: () => {
    try {
      sessionStorage.removeItem('kibo_auth_tokens');
    } catch {}
    sessionStorage.removeItem('cms_trial_popup_shown');
    set({ user: null, permissions: [], isAuthenticated: false });
    // Send the user back to the Keycloak login form rather than the landing
    // page. There is no in-app login route, so we return to a protected path:
    // the SSO session is gone by then, and ProtectedRoute starts a fresh
    // keycloak.login(), which lands on the hosted login form.
    void keycloak.logout({
      redirectUri: `${window.location.origin}${ROUTES.HOME}`,
    });
  },

  getToken: async () => {
    const existingToken = keycloak.token ?? null;
    if (!keycloak.authenticated && !existingToken) {
      return null;
    }

    try {
      if (keycloak.authenticated) {
        await keycloak.updateToken(30);
      }
    } catch (error) {
      // Keep current token if refresh fails transiently to avoid auth redirect loops.
      console.warn('Token refresh failed, falling back to existing token', error);
    }

    return keycloak.token ?? existingToken;
  },

  refreshSession: async (minValidity = 30) => {
    if (keycloak.authenticated) {
      try {
        await keycloak.updateToken(minValidity);
      } catch (error) {
        console.warn('Token refresh failed during session refresh', error);
      }
    }
    const tokenUser = getUserFromToken();
    set((state) => ({
      user: tokenUser
        ? {
            ...tokenUser,
            ...(state.user?.name ? { name: state.user.name } : {}),
            avatarUrl: state.user?.avatarUrl ?? null,
            bannerUrl: state.user?.bannerUrl ?? null,
          }
        : null,
      isAuthenticated: Boolean(tokenUser),
      isInitialized: true,
    }));
  },

  hasRole: (role: string) => get().user?.roles.includes(role) ?? false,
  hasAnyRole: (roles: string[]) => roles.some((r: string) => get().user?.roles.includes(r) ?? false),

  /**
   * Permissions come from the server (GET /api/users/me), never from a table
   * compiled into the bundle: roles are data now, so the client cannot know
   * what a role means. platform_admin resolves to the '*' wildcard.
   *
   * This is UX only. Hiding a button is not access control — the API re-checks
   * every request.
   */
  hasPermission: (permission: string) => {
    const { permissions } = get();
    return permissions.includes('*') || permissions.includes(permission);
  },

  hasAnyPermission: (permissions: string[]) =>
    permissions.some((permission) => get().hasPermission(permission)),
  hasAllPermissions: (permissions: string[]) =>
    permissions.every((permission) => get().hasPermission(permission)),
}));
